import type { RefObject } from "react";

import { useCallback, useEffect, useRef, useState } from "react";

import { clamp } from "@/app/_ui/lib/math";

import { REDUCED_MOTION_QUERY } from "./ImpactArtifactsGallery.utils";

type GalleryScrollerRef = RefObject<HTMLDivElement | null>;

const SCROLL_EPSILON = 1;

export function useImpactArtifactsScrollControls(scrollerRef: GalleryScrollerRef, itemCount: number) {
  const frameRef = useRef<number | null>(null);
  const snapOffsetsRef = useRef<number[]>([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(itemCount > 1);

  const syncScrollState = useCallback(() => {
    const scroller = scrollerRef.current;

    if (!scroller) {
      return;
    }

    const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);

    setCanScrollPrev(scroller.scrollLeft > SCROLL_EPSILON);
    setCanScrollNext(scroller.scrollLeft < maxScrollLeft - SCROLL_EPSILON);
  }, [scrollerRef]);

  const measureSnapOffsets = useCallback(() => {
    const scroller = scrollerRef.current;

    if (!scroller) {
      return;
    }

    const scrollPaddingInlineStart = readScrollPaddingInlineStart(scroller);
    const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const snapItems = Array.from(scroller.querySelectorAll<HTMLElement>("[data-snap-item]"));

    snapOffsetsRef.current = dedupeOffsets(
      snapItems.map(item => clamp(item.offsetLeft - scrollPaddingInlineStart, 0, maxScrollLeft)),
    );

    syncScrollState();
  }, [scrollerRef, syncScrollState]);

  useEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) {
      return;
    }

    measureSnapOffsets();

    const resizeObserver = new ResizeObserver(measureSnapOffsets);

    resizeObserver.observe(scroller);

    return () => {
      resizeObserver.disconnect();
    };
  }, [itemCount, measureSnapOffsets, scrollerRef]);

  useEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) {
      return;
    }

    const handleScroll = () => {
      if (frameRef.current !== null) {
        return;
      }

      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        syncScrollState();
      });
    };

    scroller.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scroller.removeEventListener("scroll", handleScroll);

      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [scrollerRef, syncScrollState]);

  const scrollByStep = useCallback(
    (direction: -1 | 1) => {
      const scroller = scrollerRef.current;
      const snapOffsets = snapOffsetsRef.current;

      if (!scroller || snapOffsets.length <= 1) {
        return;
      }

      const currentIndex = findNearestIndex(snapOffsets, scroller.scrollLeft);
      const nextIndex = clamp(currentIndex + direction, 0, snapOffsets.length - 1);

      scroller.scrollTo({
        behavior: resolveScrollBehavior(),
        left: snapOffsets[nextIndex],
      });
    },
    [scrollerRef],
  );

  return {
    canScrollNext,
    canScrollPrev,
    scrollByStep,
  };
}

function dedupeOffsets(offsets: number[]) {
  return offsets.filter((offset, index) => index === 0 || Math.abs(offset - offsets[index - 1]) > SCROLL_EPSILON);
}

function findNearestIndex(offsets: number[], scrollLeft: number) {
  if (offsets.length === 0) {
    return 0;
  }

  let nearestIndex = 0;
  let smallestDistance = Number.POSITIVE_INFINITY;

  for (let index = 0; index < offsets.length; index += 1) {
    const distance = Math.abs(offsets[index] - scrollLeft);

    if (distance < smallestDistance - SCROLL_EPSILON) {
      smallestDistance = distance;
      nearestIndex = index;
      continue;
    }

    if (Math.abs(distance - smallestDistance) <= SCROLL_EPSILON && offsets[index] <= scrollLeft + SCROLL_EPSILON) {
      nearestIndex = index;
    }
  }

  return nearestIndex;
}

function readScrollPaddingInlineStart(scroller: HTMLElement) {
  const computedStyle = window.getComputedStyle(scroller);
  const value =
    computedStyle.getPropertyValue("scroll-padding-inline-start") ||
    computedStyle.getPropertyValue("scroll-padding-left");

  return Number.parseFloat(value) || 0;
}

function resolveScrollBehavior() {
  if (window.matchMedia(REDUCED_MOTION_QUERY).matches) {
    return "auto";
  }

  return "smooth";
}
